<script setup lang="ts">
import type { BreadcrumbItem } from "@nuxt/ui";
import { getSiteDetail, getObservationSpecieList, getParticipationList } from "@carnet/api/backoffice";
import { throwOnNotFound } from "@/utils/api";

definePageMeta({
  name: "sites.detail",
  layout: "app",
});

const { t } = useI18n();
const localePath = useLocalePath();
const route = useRoute("sites.detail");
const { photosTo, detailTo } = useObservationSpecieLinks(() => String(route.params.id));
const { mapbox: mapboxAllowed } = useCookieConsent();
const { loggedIn } = useUserSession();
const isMobile = useIsMobile();

const {
  data: site,
  pending,
  error,
  refresh,
} = await getSiteDetail({
  composable: "useFetch",
  key: `site-${route.params.id}`,
  path: { locale: appLocale(), id: String(route.params.id) },
});

throwOnNotFound(error.value, "Site introuvable");

const detail = computed(() => site.value?.data);

const pageTitle = computed(() => detail.value?.name ?? t("sites.title"));

const fallbackDescription = computed(() => {
  const name = detail.value?.name ?? t("sites.title");
  const city = detail.value?.nearbyCityLabel;
  return city ? t("seo.site.description.withCity", { name, city }) : t("seo.site.description.noCity", { name });
});

useSeoMeta({
  title: () => formatPageTitle(pageTitle.value, t("sites.title")),
  description: () => metaDescription(detail.value?.description) ?? fallbackDescription.value,
});

const breadcrumb = computed<BreadcrumbItem[]>(() => [
  { label: t("home.title"), to: localePath({ name: "index" }) },
  { label: t("sites.title"), to: localePath({ name: "sites" }) },
  { label: pageTitle.value },
]);

const { mapMarkers, mapCenter, directionsUrl } = useAppSiteDetailView(detail);

useJsonLd(() => {
  const site = detail.value;
  if (!site) return null;
  return {
    "@type": "Place",
    name: site.name,
    description: metaDescription(site.description),
    address: site.nearbyCityLabel ? { "@type": "PostalAddress", addressLocality: site.nearbyCityLabel } : undefined,
    geo: site.latitude && site.longitude ? { "@type": "GeoCoordinates", latitude: site.latitude, longitude: site.longitude } : undefined,
  };
});

useSeoImage(() => {
  if (!mapCenter.value) return null;
  return {
    url: mapboxStaticImageUrl({
      positioning: mapboxStaticPositioning(mapCenter.value, 12),
      ...SEO_MAP_IMAGE_SIZE,
      markers: mapMarkers.value,
    }),
    alt: pageTitle.value,
    ...SEO_MAP_IMAGE_SIZE,
  };
});

// Section secondaire (top espèces) : ne bloque ni le SSR ni la navigation.
const { data: topSpeciesData } = await getObservationSpecieList({
  composable: "useLazyFetch",
  key: `site-${route.params.id}-top-species`,
  path: { locale: appLocale() },
  query: { siteId: Number(route.params.id), sort: "observations_desc", size: 3, page: 1 },
});

const topSpecies = computed(() => topSpeciesData.value?.data?.list ?? []);
const topSpeciesTotal = computed(() => topSpeciesData.value?.data?.pagination?.total ?? 0);

// Activités de l'utilisateur authentifié sur ce site (page publique → fetch conditionnel).
const { data: siteActivitiesData } = await useAsyncData(
  `site-${route.params.id}-activities`,
  async () => {
    if (!loggedIn.value) return null;
    const response = await getParticipationList({
      composable: "$fetch",
      path: { locale: appLocale() },
      query: { siteId: Number(route.params.id), size: 3, page: 1, sort: "date_desc", invitationStatus: "accepted" },
    });
    return response?.data ?? null;
  },
  { watch: [() => route.params.id] }
);

const siteActivities = computed(() => siteActivitiesData.value?.list ?? []);
const siteActivitiesTotal = computed(() => siteActivitiesData.value?.pagination?.total ?? 0);
</script>

<template>
  <div class="grow flex flex-col">
    <AppSection class="bg-elevated" :ui="{ base: 'pt-8' }">
      <WebBreadcrumbs :items="breadcrumb" />
    </AppSection>

    <AppContentSpinner v-if="pending" class="bg-elevated grow" />

    <AppSection v-else-if="error" data-testid="site-detail-error" class="bg-elevated grow" :ui="{ base: 'py-8' }">
      <WebErrorRetry retry-testid="site-detail-retry" @retry="refresh()" />
    </AppSection>

    <template v-else-if="detail">
      <AppSiteDetailHero :detail="detail">
        <template v-if="mapCenter" #map>
          <WebStaticMap
            :center="mapCenter"
            :zoom="12"
            :markers="mapMarkers"
            :alt="detail.name"
            priority
            class="aspect-square sm:aspect-4/3 w-full h-auto"
          >
            <div class="absolute right-6 bottom-6 flex items-center gap-2">
              <WebMapBrowseButton :center="mapCenter" :zoom="12" />

              <UButton
                v-if="mapboxAllowed && directionsUrl"
                :to="directionsUrl"
                target="_blank"
                rel="noopener"
                trailing-icon="icon:go"
                variant="soft"
                color="neutral"
              >
                {{ t("site.goThere") }}
              </UButton>
            </div>
          </WebStaticMap>
        </template>
      </AppSiteDetailHero>

      <AppSiteAbout :detail="detail" />

      <AppSection
        v-if="topSpecies.length"
        class="bg-inverted bg-ocean bg-size-[100%_150%] bg-position-[center_100%] text-inverted -mb-4 overflow-hidden"
        :ui="{ base: 'py-16 lg:pt-20 lg:pb-25' }"
      >
        <AppCommunityObservations
          :title="t('site.species.title')"
          :items="topSpecies"
          :item-key="(specie) => specie.id"
          :see-all-to="$localePath({ name: 'observations', query: { siteId: route.params.id, view: 'map' } })"
          :see-all-label="t('site.species.allCount', topSpeciesTotal, { named: { count: topSpeciesTotal } })"
        >
          <template #default="{ item }">
            <AppTileObservationSpecie :specie="item" :photos-to="photosTo(item)" :detail-to="detailTo(item)" />
          </template>
        </AppCommunityObservations>
      </AppSection>

      <AppSiteActivities
        v-if="loggedIn && siteActivities.length"
        :items="siteActivities"
        :total="siteActivitiesTotal"
        :is-mobile="isMobile"
        :see-all-to="$localePath({ name: 'activities', query: { view: 'map', siteId: route.params.id } })"
        :tile-to="(participation) => $localePath({ name: 'activities.detail', params: { id: participation.id } })"
      />
    </template>

    <p v-else class="bg-elevated grow px-10 py-8 text-blue-600">{{ t("sites.none") }}</p>
  </div>
</template>
